import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { storeAuthToken, getAuthToken, removeAuthToken, storeUserData, getUserData, clearAuthData } from '../utils/security';
import { handleApiError, handleAuthError, ErrorTypes } from '../utils/errorHandler';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(true); // Default to true to avoid blocking UI initially
  const [token, setToken] = useState(() => {
    // Initialize token from security utility with validation
    const storedToken = getAuthToken();
    // If token is invalid or expired, it will return null
    return storedToken;
  });

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
    
    // Flag to prevent multiple logout messages
    let isLoggingOut = false;
    
    // Set up axios interceptors for handling token expiration
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Only handle 401 errors if we're not already logging out and have a token
        // Skip this for login/register endpoints to allow proper error handling there
        const isAuthEndpoint = error.config && (
          error.config.url.includes('/auth/login') || 
          error.config.url.includes('/auth/register')
        );
        
        if (error.response && error.response.status === 401 && token && !isLoggingOut && !isAuthEndpoint) {
          // Token expired or invalid
          isLoggingOut = true;
          logout();
          // Use a more specific message for token expiration
          toast.error('Your session has expired. Please log in again.');
          // Prevent the error from propagating to other handlers
          return Promise.reject(new Error('Session expired'));
        }
        return Promise.reject(error);
      }
    );

    return () => {
      // Remove the interceptor when the component unmounts
      axios.interceptors.response.eject(interceptor);
    };
  }, [token]);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      if (token && !user) {
        // Try to get user from localStorage first for faster loading
        const cachedUser = getUserData();
        if (cachedUser) {
          setUser(cachedUser);
        }
        
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/me`);
          if (response.data.success && response.data.data) {
            setUser(response.data.data);
            storeUserData(response.data.data);
          } else {
            throw new Error('Invalid response format');
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          // If we get a 401 error, the token is invalid or expired
          if (error.response && error.response.status === 401) {
            toast.error('Your session has expired. Please log in again.');
          }
          clearAuthData();
          setToken(null);
          setUser(null);
        }
      } else if (!token) {
        // No token, ensure user is cleared
        if (user) {
          // User was set but token is gone, clear everything
          clearAuthData();
          setUser(null);
        } else {
          // No token and no user, just ensure user is cleared
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token, user]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/login`, { email, password });
      
      if (response.data.success && response.data.data) {
        const { token: newToken, user: userData } = response.data.data;
        
        // Check if email is verified
        if (!userData.isEmailVerified) {
          setEmailVerified(false);
          toast.error('Please verify your email before logging in. Check your inbox for a verification link.');
          return { success: false, error: 'Email not verified', needsVerification: true, email };
        }
        
        // Store token and user data securely
        storeAuthToken(newToken);
        storeUserData(userData);
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        // Update state
        setToken(newToken);
        setUser(userData);
        setEmailVerified(true);
        
        toast.success('Login successful!');
        return { success: true, user: userData };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      handleApiError(error);
      const message = error.response?.data?.message || error.message || 'Login failed';
      const errorType = error.response?.data?.errorType;
      
      // Handle different error types
      switch (errorType) {
        case 'emailNotVerified':
          setEmailVerified(false);
          return { 
            success: false, 
            error: message, 
            needsVerification: true,
            errorType,
            email: email 
          };
        case 'accountDeactivated':
          return {
            success: false,
            error: message,
            errorType,
            deactivated: true
          };
        case 'invalidCredentials':
        default:
          return {
            success: false,
            error: message,
            errorType: errorType || 'unknown'
          };
      }
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/register`, userData);
      
      if (response.data.success && response.data.data) {
        const { token: newToken, user: newUser, requireEmailVerification } = response.data.data;
        
        // Store token and user data securely
        storeAuthToken(newToken);
        storeUserData(newUser);
        
        setToken(newToken);
        setUser(newUser);
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        if (requireEmailVerification) {
          setEmailVerified(false);
          toast.success('Registration successful! Please check your email to verify your account.');
          return { success: true, requireEmailVerification: true, email: userData.email };
        } else {
          setEmailVerified(true);
          toast.success('Registration successful!');
          return { success: true };
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      handleApiError(error);
      const message = error.response?.data?.message || error.message || 'Registration failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    // Clear auth data securely
    clearAuthData();
    
    // Clear state
    setToken(null);
    setUser(null);
    
    // Clear axios default header
    delete axios.defaults.headers.common['Authorization'];
    
    toast.success('Logged out successfully');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/profile`, profileData);
      setUser(response.data.data);
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/change-password`, {
        currentPassword,
        newPassword
      });
      toast.success('Password changed successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/resend-verification`,
        { email }
      );
      
      if (response.data.success) {
        toast.success('Verification email sent! Please check your inbox.');
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Failed to resend verification email');
      }
    } catch (error) {
      handleApiError(error);
      const message = error.response?.data?.message || error.message || 'Failed to resend verification email';
      return { success: false, error: message };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/forgot-password`,
        { email }
      );
      
      if (response.data.success) {
        toast.success('Password reset instructions sent to your email!');
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Failed to send reset email');
      }
    } catch (error) {
      // Don't reveal if email exists or not for security
      // Don't use handleApiError here as we don't want to show the actual error
      console.error('Forgot password error:', error);
      toast.success('If your email exists in our system, you will receive reset instructions shortly.');
      return { success: true }; // Always return success for security
    }
  };

  const resetPassword = async (token, password) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/reset-password`,
        { token, password }
      );
      
      if (response.data.success) {
        toast.success('Password has been reset successfully!');
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      handleApiError(error);
      const message = error.response?.data?.message || error.message || 'Failed to reset password';
      return { success: false, error: message };
    }
  };

  const loginWithGoogle = async () => {
    // Redirect to Google OAuth route
    window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/google`;
  };

  const value = {
    user,
    loading,
    emailVerified,
    isAuthenticated: !!user && !!token,
    isAdmin: user?.role === 'admin',
    isInstructor: user?.role === 'instructor',
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    resendVerificationEmail,
    forgotPassword,
    resetPassword,
    loginWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

